import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { CATEGORY_COLORS, TORCH_RED, DEFAULT_CATEGORY_COLOR, getPrimaryCategory, SearchableDashboard } from "../../constants";
import { useFavouriteDashboards } from "../../hooks";
import {
  useMyDashboardAccessRequests,
  useSubmitDashboardAccessRequest,
} from "../../hooks/react-query/access-requests/useMyDashboardAccessRequests";
import { CedarShellSidenavContext } from "../Layouts/CedarShell/CedarShellSidenavContext";
import "../../assets/styles/component/dashboardCard.scss";

// Last-resort fallback for the rare case a dashboard's own `description` (now a real GET
// /dashboards field, see SearchableDashboard) comes back empty. Keyed to the current real
// categories (ids 13-17 per GET /dashboards/dashboard_category_list) - replaces the older
// Experience/Shop/Use/Executive Reporting/Analytics Tools set those categories were renamed from.
const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  "Customer Experience": "Awareness, brand perception and voice of customer",
  AMIEO: "Reporting and dashboards for the AMIEO region",
  "Marketing Intelligence": "Ad hoc analytics and reporting tools",
  "Connected Car Services": "Connected vehicle and telematics services data",
  "Marketing and Sales": "Vehicle sales funnel data and product marketing",
};

const hexToRgba = (hex: string, alpha: number): string => {
  const sanitized = hex.replace("#", "");
  const bigint = parseInt(sanitized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface Props {
  dashboard: SearchableDashboard;
  description?: string;
  // Fired just before navigating, however the card was opened (body click, Open button, keyboard,
  // ctrl/cmd-click). Only the Dashboards page passes it, to record a search result being chosen.
  onOpen?: () => void;
}

// Reused by both the Favourites and Dashboards pages - deliberately self-contained (manages its
// own favourite state and navigation) so either page only needs to render <DashboardCard dashboard={d} />.
export const DashboardCard = (props: Props) => {
  const { dashboard, description, onOpen } = props;
  const history = useHistory();
  const { isFavourite, toggleFavourite } = useFavouriteDashboards();
  const { myRequests } = useMyDashboardAccessRequests();
  const submitAccessRequest = useSubmitDashboardAccessRequest();
  const { setCollapsed } = useContext(CedarShellSidenavContext);

  // Ctrl (Windows/Linux) / Cmd (Mac) + click (or +Enter) opens in a new tab, matching how a real
  // <a href> link already behaves everywhere else - default click still navigates in place via
  // history.push (no full page reload). Neither the card body nor the Open button is a real anchor
  // (both host other nested interactive elements - favourite/Request access - which isn't valid
  // inside a real <a>), so this is replicated manually rather than relying on native anchor
  // click-modifier handling.
  const openDashboard = (newTab: boolean) => {
    onOpen?.();
    if (newTab) {
      // A real new tab/window at this same-origin path re-authenticates from the existing session
      // cookie, the same as pasting the URL directly - noopener/noreferrer since this isn't a
      // trusted external link, just this app's own SPA reloading at a different route.
      window.open(dashboard.path, "_blank", "noopener,noreferrer");
      return;
    }
    setCollapsed(true);
    history.push({ pathname: dashboard.path, search: "" });
  };

  const handleOpen = (event: React.MouseEvent) => openDashboard(event.ctrlKey || event.metaKey);

  // Stops a click on this element from also reaching the card's own onClick (handleOpen) below -
  // needed on every nested interactive element (favourite/Open/Request access) now that the whole
  // card navigates, not just the Open button. Without this, e.g. toggling favourite would ALSO
  // navigate away, and clicking Open itself would call handleOpen twice for the same click.
  const stopCardClick = (event: React.MouseEvent) => event.stopPropagation();

  const handleRequestAccess = (event: React.MouseEvent) => {
    stopCardClick(event);
    submitAccessRequest.mutate(dashboard.id, {
      onSuccess: () => toast.success(`Request sent for ${dashboard.name}`),
    });
  };

  const handleCardKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openDashboard(event.ctrlKey || event.metaKey);
    }
  };

  const favourited = isFavourite(dashboard.key);
  const accessPending = myRequests.some((request) => request.dashboard_id === dashboard.id && request.status === "pending");
  // An explicit description prop (if a caller ever passes one) wins, then the dashboard's own real
  // description, then the category blurb as a last resort for the rare empty one.
  const resolvedDescription = description ?? dashboard.description ?? CATEGORY_DESCRIPTIONS[getPrimaryCategory(dashboard)];

  return (
    <div
      className={`dashboard_card ${dashboard.has_access ? "dashboard_card--clickable" : ""}`}
      data-test-id={`dashboard-card-${dashboard.key}`}
      // Clicking the card itself now opens the dashboard, same destination as the explicit Open
      // button below - matches DashboardCard being the one place both the Dashboards and
      // Favourites pages render a tile, so this fixes the click behavior everywhere at once. Only
      // wired when the user actually has somewhere to go; a restricted card's only real action is
      // its own Request access button (which stops its click from reaching here - see
      // stopCardClick).
      onClick={dashboard.has_access ? handleOpen : undefined}
      onKeyDown={dashboard.has_access ? handleCardKeyDown : undefined}
      role={dashboard.has_access ? "button" : undefined}
      tabIndex={dashboard.has_access ? 0 : undefined}
    >
      <div className="dashboard_card_top">
        <div className="dashboard_card_tags">
          {dashboard.categories.map((category) => {
            const pillColor = CATEGORY_COLORS[category.name] ?? DEFAULT_CATEGORY_COLOR;
            return (
              <span
                key={category.id}
                className="dashboard_card_pill"
                style={{ color: pillColor, backgroundColor: hexToRgba(pillColor, 0.15) }}
              >
                {category.name}
              </span>
            );
          })}
        </div>
        {/* No favouriting a dashboard the user can't open - the toggle just doesn't exist here,
            rather than existing disabled. */}
        {dashboard.has_access && (
          <button
            className="dashboard_card_favourite"
            onClick={(event) => {
              stopCardClick(event);
              toggleFavourite(dashboard);
            }}
            aria-label={favourited ? "Remove from favourites" : "Add to favourites"}
            data-test-id={`dashboard-card-favourite-${dashboard.key}`}
          >
            {favourited ? <StarIcon style={{ color: TORCH_RED }} /> : <StarBorderIcon />}
          </button>
        )}
      </div>

      <h3 className="dashboard_card_name">{dashboard.name}</h3>
      {resolvedDescription && <p className="dashboard_card_description">{resolvedDescription}</p>}

      <div className="dashboard_card_actions">
        {/* Same reasoning as the favourite toggle above - no Open button at all for a dashboard
            the user can't open, rather than existing disabled. Still its own button (not just
            relying on the card's new onClick) - keeps a keyboard/screen-reader-visible affordance
            and stops double-firing handleOpen via stopCardClick. */}
        {dashboard.has_access && (
          <button
            className="dashboard_card_open"
            onClick={(event) => {
              stopCardClick(event);
              handleOpen(event);
            }}
            data-test-id={`dashboard-card-open-${dashboard.key}`}
          >
            Open
          </button>
        )}
        {!dashboard.has_access && (
          <button
            className="dashboard_card_request_access"
            onClick={handleRequestAccess}
            disabled={accessPending || submitAccessRequest.isLoading}
            data-test-id={`dashboard-card-request-access-${dashboard.key}`}
          >
            {accessPending ? "Access requested" : "Request access"}
          </button>
        )}
      </div>
    </div>
  );
};
